import { useRef } from 'react';

import { CartPayModeModel } from '@/models';

import { useAppDispatch, useAppSelector } from '@/redux';
import { selectPaymentMode } from '@/redux/slices/cart.slice';
import { selectAvailablePaymentModes } from '@/redux/slices/checkout.slice';

import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';

import CreditCardPayMode from '@/components/checkout/payMode/CreditCardPayMode';
import Divider from '@/components/common/Divider';
import Icon from '@/components/common/iconedLinks/Icon';
import PaymentPending from '@/components/checkout/common/PaymentPending';
import RadioButton from '@/components/common/RadioButton';
import Tile from '@/components/common/Tile';

import styles from './PaymentSection.module.scss';

export const PaymentSection = () => {
  const dispatch = useAppDispatch();
  const previousPromise = useRef<any>(null);
  const paymentModesForStoreId = useAppSelector(selectAvailablePaymentModes);
  const paymentModeSelected = useAppSelector(selectPaymentMode);
  const isPaymentModeSelected = (currentPaymentId: string) => paymentModeSelected?._id === currentPaymentId;
  const scrollToTile = useSmoothScroll();

  const onSelectionChange = (newPaymentModeSelected: CartPayModeModel) => {
    if (paymentModeSelected?._id === newPaymentModeSelected._id) {
      return;
    }

    if (previousPromise.current) {
      previousPromise.current.abort();
    }
    previousPromise.current = dispatch(
      updateCartAttributeByName({ attributeName: 'paiementMode', newValue: newPaymentModeSelected })
    );
  };

  const updateCardInfo = (cardInformation: CartPayModeModel, toBeSave: boolean) => {
    const newValue = {
      ...paymentModeSelected,
      cardInformation,
      toBeSave
    };
    updateCartAttributeByName({ attributeName: 'paiementMode', newValue });
  };

  const isPaymentBeingProcessedOrChecked = useAppSelector(({ checkout }) => checkout.paymentStatus.status === 'pending');

  if (isPaymentBeingProcessedOrChecked) {
    return <PaymentPending />;
  }

  return (
    <>
      {paymentModesForStoreId.map((paymentMode) => {
        const { _id, logos, name } = paymentMode;
        return (
          <>
            <Tile
              dataRbf={_id}
              key={_id}
              onClick={() => onSelectionChange(paymentMode)}
              selected={isPaymentModeSelected(_id)}
              alignToTop={(ref) => scrollToTile(ref, _id, paymentModeSelected?._id)}
              leadingContent={
                <RadioButton
                  id={`${paymentMode._id}-radio`}
                  isChecked={isPaymentModeSelected(_id)}
                  customClassName={styles['radio-button']}
                />
              }
              trailingContent={
                <>
                  {logos && logos.length > 0 && (
                    <div className={styles.logos}>
                      {logos.map((logo, index) => (
                        <Icon key={index} src={logo} alt={_id} width={32} height={32}/>
                      ))}
                    </div>
                  )}
                </>
              }
              content={{
                label: name,
                component: (paymentModeSelected?._id === paymentMode._id) && (
                  <>
                    <Divider spacing={16} />
                    <CreditCardPayMode
                      updateCardInfo={updateCardInfo}
                      paymentMode={paymentMode}
                      isDisplayableItem={isPaymentModeSelected}
                    />
                  </>
                )
              }}
            />
          </>
        );
      })}
    </>
  );
};
